import React, { createContext, useCallback, useContext, useEffect, useMemo } from "react";
import { useSnackbar } from "notistack";
import semver from "semver";
import Button from "@mui/material/Button";
import { fetchConfig } from "./config-service.js";
import { APP_VERSION } from "./app-version.js";
import { useQuery } from "@tanstack/react-query";

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * @typedef {import("@seabrookstudios/pitch2table-core").ServerConfig} ServerConfig
 */

/**
 * @type {ServerConfig}
 */
const initialState = {
  version: "0.0.0",
  minWebVersion: "10000.0.0",
  sha: "not set",
  conventionCalendarEvents: {
    GenCon: undefined,
    UKGE: undefined,
    Essen: undefined,
    Origins: undefined,
    "Dice Tower": undefined,
    "Mojo Pitch": undefined,
    PlayCon: undefined,
    "Asia Board Games Festival": undefined,
    "Tokyo Game Market": undefined,
    ChiTAG: undefined,
    PAXU: undefined,
  },
};

const ConfigContext = createContext(initialState);

export const useServerConfig = () => {
  return useQuery({
    queryKey: ["server-config"],
    queryFn: async () => {
      const config = await fetchConfig();
      return config;
    },
    staleTime: POLL_INTERVAL_MS,
  });
};

const ConfigProvider = ({ children }) => {
  const { enqueueSnackbar } = useSnackbar();

  const { data: serverConfig } = useServerConfig();
  const isUpdateAvailable = useMemo(() => {
    return serverConfig && semver.lt(APP_VERSION, serverConfig.minWebVersion);
  }, [serverConfig, APP_VERSION]);

  const showUpdateBanner = useCallback(() => {
    enqueueSnackbar("A new version of this app is available.", {
      variant: "info",
      persist: true,
      preventDuplicate: true,
      action: () => (
        <Button color="inherit" size="small" onClick={() => window.location.reload()}>
          Refresh
        </Button>
      ),
    });
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (isUpdateAvailable) {
      showUpdateBanner();
    }
  }, [isUpdateAvailable]);

  const value = useMemo(
    () => ({
      ...serverConfig,
    }),
    [serverConfig]
  );

  return <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>;
};

export const useConfig = () => useContext(ConfigContext);

export default ConfigProvider;
